import { AnimationMixer, MathUtils } from 'three';

let playerAnim = null;

export function initPlayerAnim(gltfScene) {
  const root = gltfScene?.scene || gltfScene;
  const clips = gltfScene?.animations || root?.animations || [];
  const mixer = root && clips.length ? new AnimationMixer(root) : null;
  const actions = {};
  for (const clip of clips) actions[clip.name] = mixer.clipAction(clip).play();
  playerAnim = { root, mixer, actions, hurt: 0, shot: 0, dead: false, time: 0 };
  return playerAnim;
}

export function updatePlayerAnim(velocity, grounded = true, dt = 0) {
  if (!playerAnim) return;
  playerAnim.time += dt;
  playerAnim.mixer?.update(dt);
  playerAnim.hurt = Math.max(0, playerAnim.hurt - dt * 3.3);
  playerAnim.shot = Math.max(0, playerAnim.shot - dt * 8.5);

  const speed = velocity?.length?.() || 0;
  const run = MathUtils.clamp(speed / 8, 0, 1);

  // Loaded GLTF hero chibi — procedural hop/wobble
  const chibi = playerAnim.root?.children?.find?.((c) => c.userData?.heroModel);
  if (chibi && !playerAnim.mixer) {
    animateHeroModel(chibi, velocity, run, grounded);
    return;
  }

  // Procedural skeleton fallback
  const parts = playerAnim.root?.userData?.parts;
  if (!parts || playerAnim.mixer) return;

  const stride = Math.sin(playerAnim.time * MathUtils.lerp(5, 13, run)) * run;
  const hurtKick = playerAnim.hurt * 0.45;
  if (parts.leftArm) parts.leftArm.rotation.x = stride * 0.45 - hurtKick;
  if (parts.rightArm) parts.rightArm.rotation.x = -stride * 0.45 - hurtKick;
  if (parts.leftLeg) parts.leftLeg.rotation.x = -stride * 0.55;
  if (parts.rightLeg) parts.rightLeg.rotation.x = stride * 0.55;
  if (parts.body) parts.body.rotation.z = Math.sin(playerAnim.time * 8) * run * 0.035;
  if (!grounded && parts.body) parts.body.rotation.x = -0.18;
}

function animateHeroModel(model, velocity, run, grounded) {
  const basePosition = model.userData.basePosition;
  const baseRotation = model.userData.baseRotation;
  const baseScale = model.userData.baseScale;
  const strideRate = MathUtils.lerp(4.4, 13.5, run);
  const step = Math.sin(playerAnim.time * strideRate);
  const hop = Math.abs(step) * run;
  const breathe = Math.sin(playerAnim.time * 2.2) * (1 - run);
  const yaw = playerAnim.root?.rotation?.y || 0;
  const forwardSpeed = velocity.z * Math.cos(yaw) + velocity.x * Math.sin(yaw);
  const sideSpeed = velocity.x * Math.cos(yaw) - velocity.z * Math.sin(yaw);
  const forward = MathUtils.clamp(forwardSpeed / 13, -1, 1);
  const strafe = MathUtils.clamp(sideSpeed / 13, -1, 1);
  const hurt = playerAnim.hurt;
  const shot = playerAnim.shot;

  model.position.x = (basePosition?.x || 0) - strafe * run * 0.035 + (hurt > 0.05 ? (Math.random() - 0.5) * hurt * 0.08 : 0);
  model.position.y = (basePosition?.y || 0) + hop * 0.16 + breathe * 0.025;
  model.position.z = (basePosition?.z || 0) - forward * run * 0.035 + shot * 0.08;
  model.rotation.x = (baseRotation?.x || 0) + forward * 0.18 + run * 0.07 + (grounded ? 0 : -0.22) - hurt * 0.18 - shot * 0.26;
  model.rotation.y = baseRotation?.y || 0;
  model.rotation.z = (baseRotation?.z || 0) - strafe * 0.18 + step * run * 0.075 + hurt * (Math.random() - 0.5) * 0.4;

  const squash = hop * 0.075 + hurt * 0.045 + shot * 0.035;
  model.scale.set(
    (baseScale?.x || 1) * (1 + squash),
    (baseScale?.y || 1) * (1 - squash * 0.72),
    (baseScale?.z || 1) * (1 + squash * 0.55),
  );
}

export function triggerHurt() {
  if (playerAnim) playerAnim.hurt = 1;
}

export function triggerShoot(power = 1) {
  if (playerAnim) playerAnim.shot = Math.max(playerAnim.shot || 0, Math.min(1, 0.55 * power));
}

export function triggerDeath() {
  if (playerAnim) playerAnim.dead = true;
}
